import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import {
  Box, Button, Collapse, FormControl, IconButton, InputLabel, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, Menu, ListItemIcon, ListItemText, Pagination, Checkbox,
  CircularProgress, Alert
} from '@mui/material';
import { ExpandMore, ExpandLess, Search, History, MoreVert, List, Edit, Add, Refresh } from '@mui/icons-material';
import Navbar from '../Layouts/Navbar';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import HistoriqueModal from './HistoriqueModal';
import GroupsIcon from "@mui/icons-material/Groups";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddModal from './AddModal';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import api from '../../utils/api';



export default function UtilisateursTable() {
  const [openhis, setOpenHis] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const handleOpenHis = (user) => {
    setSelectedUser(user);
    setOpenHis(true);
  };
  const handleCloseHis = () => {
    setOpenHis(false);
    setSelectedUser(null);
  };

  const [openadd, setOpenAdd] = useState(false);
  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => {
    setOpenAdd(false);
    fetchUsers(); // Refresh the list after adding a new user
  };

  const [snack, setSnack] = useState({ open: false, message: '', type: 'success' });
  const handleSnack = (message, type = 'success') => {
    setSnack({ open: true, message, type });
  };
  
  const handleCloseSnack = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnack({ ...snack, open: false });
  };

  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [menuRowId, setMenuRowId] = useState(null);

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  
  // State for selected rows/checkboxes
  const [selected, setSelected] = useState([]);
  const [groupAction, setGroupAction] = useState('');
  
  // State for users data
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sites, setSites] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [societesLoading, setSocietesLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [rolesError, setRolesError] = useState(false);
  const [sitesError, setSitesError] = useState(false);
  const [societesError, setSocietesError] = useState(false);
  const [error, setError] = useState(null);
  
  // State for search filters
  const [filters, setFilters] = useState({
    keyword: '',
    status: '',
    role: '',
    site: ''
  });
  
  // Fetch users from API
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      const usersData = response.data;
      
      // Enhance users with societe data
      const enhancedUsers = await Promise.all(usersData.map(async (user) => {
        // If user has site data with ID_Societe
        if (user.site && user.site.ID_Societe) {
          try {
            // Fetch societe data
            const societeResponse = await api.get(`/societes/${user.site.ID_Societe}`);
            // Return user with societe data
            return {
              ...user,
              societe: societeResponse.data
            };
          } catch (err) {
            console.error(`Error fetching societe for user ${user.ID_User}:`, err);
            // Return user without societe data
            return user;
          }
        }
        // Return user as is if no site or ID_Societe
        return user;
      }));
      
      setUsers(enhancedUsers);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
      handleSnack('Failed to load users. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch roles from API
  const fetchRoles = async (showErrorMessage = true) => {
    setRolesLoading(true);
    setRolesError(false);
    try {
      const response = await api.get('/roles');
      setRoles(response.data);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setRolesError(true);
      if (showErrorMessage) {
        handleSnack('Failed to load roles. Please try again.', 'error');
      }
    } finally {
      setRolesLoading(false);
    }
  };
  
  // Retry fetching roles
  const retryFetchRoles = () => {
    fetchRoles(true);
  };
  
  // Fetch sites from API
  const fetchSites = async (showErrorMessage = true) => {
    setSitesLoading(true);
    setSitesError(false);
    try {
      const response = await api.get('/sites');
      setSites(response.data);
    } catch (err) {
      console.error('Error fetching sites:', err);
      setSitesError(true);
      if (showErrorMessage) {
        handleSnack('Failed to load sites. Please try again.', 'error');
      }
    } finally {
      setSitesLoading(false);
    }
  };
  
  // Retry fetching sites
  const retryFetchSites = () => {
    fetchSites(true);
  };
  
  // Fetch societes from API
  const fetchSocietes = async (showErrorMessage = true) => {
    setSocietesLoading(true);
    setSocietesError(false);
    try {
      const response = await api.get('/societes');
      setSocietes(response.data);
    } catch (err) {
      console.error('Error fetching societes:', err);
      setSocietesError(true);
      if (showErrorMessage) {
        handleSnack('Failed to load societes. Please try again.', 'error');
      }
    } finally {
      setSocietesLoading(false);
    }
  };
  
  // Retry fetching societes
  const retryFetchSocietes = () => {
    fetchSocietes(true);
  };

  // Load users, roles, sites, and societes on component mount
  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchSites();
    fetchSocietes();
  }, []);

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      fetchUsers(); // Refresh the list after deletion
      handleSnack('Utilisateur supprimé avec succès', 'success');
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting user:', error);
      
      // Gérer les erreurs de validation du serveur
      if (error.response && error.response.data) {
        if (error.response.data.errors) {
          // Traitement des erreurs de validation spécifiques
          const errorMessage = Object.values(error.response.data.errors)
            .flat()
            .join('\n');
          handleSnack(errorMessage || "Erreur de validation lors de la suppression", 'error');
        } else if (error.response.data.message) {
          // Message d'erreur général
          handleSnack(error.response.data.message, 'error');
        } else {
          handleSnack("Erreur lors de la suppression de l'utilisateur", 'error');
        }
      } else {
        handleSnack("Erreur de connexion au serveur", 'error');
      }
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      // Toggle the Status between 'Actif' and 'Inactif'
      const newStatus = currentStatus ? 'Inactif' : 'Actif';
      await api.put(`/users/${userId}`, { Status: newStatus });
      fetchUsers(); // Refresh the list after status change
      handleSnack(`Utilisateur ${newStatus.toLowerCase()} avec succès`, 'success');
      handleMenuClose();
    } catch (error) {
      console.error('Error updating user status:', error);
      
      // Gérer les erreurs de validation du serveur
      if (error.response && error.response.data) {
        if (error.response.data.errors) {
          // Traitement des erreurs de validation spécifiques
          const errorMessage = Object.values(error.response.data.errors)
            .flat()
            .join('\n');
          handleSnack(errorMessage || "Erreur de validation lors de la modification du statut", 'error');
        } else if (error.response.data.message) {
          // Message d'erreur général
          handleSnack(error.response.data.message, 'error');
        } else {
          handleSnack("Erreur lors de la modification du statut de l'utilisateur", 'error');
        }
      } else {
        handleSnack("Erreur de connexion au serveur", 'error');
      }
    }
  };

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = users.map(user => user.ID_User);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleGroupAction = async () => {
    if (groupAction === 'Supprimer' && selected.length > 0) {
      try {
        // Delete multiple users
        await Promise.all(selected.map(userId => api.delete(`/users/${userId}`)));
        fetchUsers(); // Refresh the list after deletion
        handleSnack('Utilisateurs supprimés avec succès', 'success');
        setSelected([]);
        setGroupAction('');
      } catch (error) {
        console.error('Error deleting users:', error);
        
        // Gérer les erreurs de validation du serveur
        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            // Traitement des erreurs de validation spécifiques
            const errorMessage = Object.values(error.response.data.errors)
              .flat()
              .join('\n');
            handleSnack(errorMessage || "Erreur de validation lors de la suppression", 'error');
          } else if (error.response.data.message) {
            // Message d'erreur général
            handleSnack(error.response.data.message, 'error');
          } else {
            handleSnack("Erreur lors de la suppression des utilisateurs", 'error');
          }
        } else {
          handleSnack("Erreur de connexion au serveur", 'error');
        }
      }
    }
  };
  
  // Handle search filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  // Apply filters
  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      // Refresh data with current filters
      await fetchUsers();
      console.log('Applying filters:', filters);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setSearchLoading(false);
    }
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      keyword: '',
      status: '',
      role: '',
      site: ''
    });
    // Refresh roles, sites, and societes data when filters are reset
    fetchRoles();
    fetchSites();
    fetchSocietes();
  };

  // Apply client-side filtering (in a real app, this would be done on the server)
  const filteredUsers = users.filter(user => {
    if (filters.keyword && !(
      (user.name && user.name.toLowerCase().includes(filters.keyword.toLowerCase())) ||
      (user.prenom && user.prenom.toLowerCase().includes(filters.keyword.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(filters.keyword.toLowerCase())) ||
      (user.matricule && user.matricule.toString().includes(filters.keyword))
    )) {
      return false;
    }
    
    if (filters.status && user.Status !== filters.status) {
      return false;
    }
    
    if (filters.role && user.ID_Role !== parseInt(filters.role)) {
      return false;
    }
    
    if (filters.site && user.ID_Site !== parseInt(filters.site)) {
      return false;
    }
    
    return true;
  });
  
  const paginatedUsers = filteredUsers.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const pageCount = Math.ceil(filteredUsers.length / rowsPerPage);

  return (
    <Box>
      <Navbar />
      <Box p={2}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
            <GroupsIcon />
            <Typography variant="h6">UTILISATEURS</Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button variant="outlined"
             onClick={handleOpenAdd}
            sx={{
              color: 'red',
              borderColor: 'red',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
            }} startIcon={<Add />}>Ajouter</Button>
            <Button variant="outlined"
            
            component={Link}  
            to="/import-utilisateurs"
            sx={{
              color: 'red',
              borderColor: 'red',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
            }} startIcon={<FileUploadIcon />}>Importer</Button>
          </Box>
        </Box>

        {/* Search Section */}
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1} mt={1} onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
            <Search />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>RECHERCHER</Typography>
            <IconButton>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Box>
        </Paper>

        <Collapse in={open}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box display="flex" gap={2} mb={2}>
              <TextField
                placeholder="Mot Cle"
                name="keyword"
                value={filters.keyword}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#000' },
                    '&.Mui-focused fieldset': { borderColor: 'red' },
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': { color: 'red' },
                    '&:hover': { color: '#000' },
                  }
                }}
              />
              <FormControl fullWidth size="small" sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#000' },
                  '&.Mui-focused fieldset': { borderColor: 'red' },
                }
              }}>
                <InputLabel sx={{
                  '&.Mui-focused': { color: 'red' },
                  '&:hover': { color: '#000' },
                }}>Status</InputLabel>
                <Select 
                  label="Status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="Actif">Actif</MenuItem>
                  <MenuItem value="Inactif">Inactif</MenuItem>
                </Select>
              </FormControl>
              <Box display="flex" alignItems="center" width="100%">
                <FormControl fullWidth size="small" sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#000' },
                    '&.Mui-focused fieldset': { borderColor: 'red' },
                  }
                }}>
                  <InputLabel sx={{
                    '&.Mui-focused': { color: 'red' },
                    '&:hover': { color: '#000' },
                  }}>Role</InputLabel>
                  <Select 
                    label="Role"
                    name="role"
                    value={filters.role}
                    onChange={handleFilterChange}
                    disabled={rolesLoading}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {rolesLoading ? (
                      <MenuItem disabled>Chargement des rôles...</MenuItem>
                    ) : rolesError ? (
                      <MenuItem disabled sx={{ color: 'error.main' }}>
                        Erreur de chargement
                      </MenuItem>
                    ) : (
                      roles.map((role) => (
                        <MenuItem key={role.ID_Role} value={role.ID_Role.toString()}>
                          {role.Role}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <IconButton 
                  onClick={retryFetchRoles} 
                  disabled={rolesLoading}
                  sx={{ ml: 1, color: rolesError ? 'error.main' : 'inherit' }}
                  size="small"
                >
                  <Refresh />
                </IconButton>
              </Box>
            </Box>
            
            <Box display="flex" gap={2} mb={2}>
              <Box display="flex" alignItems="center" width="100%">
                <FormControl fullWidth size="small" sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#000' },
                    '&.Mui-focused fieldset': { borderColor: 'red' },
                  }
                }}>
                  <InputLabel sx={{
                    '&.Mui-focused': { color: 'red' },
                    '&:hover': { color: '#000' },
                  }}>Site</InputLabel>
                  <Select 
                    label="Site"
                    name="site"
                    value={filters.site}
                    onChange={handleFilterChange}
                    disabled={sitesLoading}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {sitesLoading ? (
                      <MenuItem disabled>Chargement des sites...</MenuItem>
                    ) : sitesError ? (
                      <MenuItem disabled sx={{ color: 'error.main' }}>
                        Erreur de chargement
                      </MenuItem>
                    ) : (
                      sites.map((site) => (
                        <MenuItem key={site.ID_Site} value={site.ID_Site.toString()}>
                          {site.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <IconButton 
                  onClick={retryFetchSites} 
                  disabled={sitesLoading}
                  sx={{ ml: 1, color: sitesError ? 'error.main' : 'inherit' }}
                  size="small"
                >
                  <Refresh />
                </IconButton>
              </Box>
            </Box>
            
            <Box display="flex" justifyContent="space-between" gap={2}>
              <Button 
                variant="outlined" 
                onClick={handleResetFilters}
                sx={{
                  color: 'red',
                  borderColor: 'red',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
                }}
              >
                Reset
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleSearch}
                disabled={searchLoading}
                sx={{
                  color: 'red',
                  borderColor: 'red',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
                }}
              >
                {searchLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Recherche...
                  </>
                ) : 'Search'}
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {/* Bulk Actions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography>Actions groupées</Typography>
            <FormControl size="small" sx={{
                minWidth: 150 ,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#000' },
                  '&.Mui-focused fieldset': { borderColor: 'red' },
                }
              }}>
              <Select
                value={groupAction}
                onChange={(e) => setGroupAction(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled><em>Sélectionner</em></MenuItem>
                <MenuItem value="Supprimer">Supprimer</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disabled={!groupAction || selected.length === 0}
              onClick={handleGroupAction}
              sx={{
                bgcolor: 'red',
                textTransform: 'none',
                '&:hover': { bgcolor: 'red' },
                '&.Mui-disabled': { bgcolor: '#f5f5f5', color: '#bdbdbd' }
              }}
            >
              Appliquer
            </Button>
          </Box>
          <Typography>{selected.length} sélectionnés / {users.length} entrées</Typography>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table>
          <TableHead>
                <TableRow>
                    <TableCell padding="checkbox">
                    <Checkbox
                        indeterminate={selected.length > 0 && selected.length < users.length}
                        checked={users.length > 0 && selected.length === users.length}
                        onChange={handleSelectAll}
                        sx={{
                        '&.MuiCheckbox-indeterminate': {
                            color: '#000',
                        },
                        '&.Mui-checked': {
                            color: '#000',
                        },
                        }}
                    />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Matricule</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Société</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Site</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Prénom</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>État</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
        </TableHead>
        <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  <CircularProgress color="error" />
                  <Typography variant="body2" sx={{ mt: 1 }}>Chargement des utilisateurs...</Typography>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  <Typography color="error">{error}</Typography>
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  <Typography>Aucun utilisateur trouvé</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => {
                const isItemSelected = isSelected(user.ID_User);
                return (
                <TableRow key={user.ID_User} hover selected={isItemSelected}>
                    <TableCell padding="checkbox">
                    <Checkbox
                        checked={isItemSelected}
                        onChange={(event) => handleSelectOne(event, user.ID_User)}
                        sx={{
                        '&.MuiCheckbox-indeterminate': {
                            color: '#000',
                        },
                        '&.Mui-checked': {
                            color: '#000',
                        },
                        }}
                    />
                    </TableCell>
                    <TableCell>{user.matricule}</TableCell>
                    <TableCell>{user.societe?.name || (user.site?.societe?.name) || '-'}</TableCell>
                    <TableCell>{user.ID_Site ? user.site?.name : '-'}</TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.prenom}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                    <Box
                        sx={{
                        backgroundColor:user.Status === 'actif' || user.Status === 'Actif' || user.Status === true || user.Status === 1 ? '#4caf50' : '#dc3545', 
                        color: 'white',
                        borderRadius: '4px',
                        px: 1,
                        py: 0.5,
                        display: 'inline-block',
                        fontSize: '0.8rem',
                        textTransform: 'lowercase',
                        whiteSpace: 'nowrap',
                        }}
                    >
                        {user.Status === 'actif' || user.Status === 'Actif' || user.Status === true || user.Status === 1 ? 'active' : 'desactive'}
                    </Box>
                    </TableCell>
                    <TableCell>
                    <IconButton onClick={(e) => handleMenuClick(e, user.ID_User)}>
                        <MoreVert />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={menuRowId === user.ID_User && openMenu}
                        onClose={handleMenuClose}
                        PaperProps={{
                        sx: {
                            minWidth: '200px',
                            '& .MuiMenuItem-root': {
                            color: 'black',
                            '& .MuiListItemIcon-root': {
                                color: 'black',
                            },
                            '&:hover': {
                                backgroundColor: 'red',
                                color: 'white',
                                '& .MuiListItemIcon-root': {
                                color: 'white',
                                },
                            },
                            },
                        },
                        }}
                    >
                        <MenuItem component={Link} to={`/utilisateurs-details/${user.ID_User}`}>
                        <ListItemIcon>
                            <List fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Détail</ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} to={`/modifier-utilisateurs/${user.ID_User}`}>
                        <ListItemIcon>
                            <Edit fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Modifier</ListItemText>
                        </MenuItem>
                        <MenuItem 
                          sx={{ color: 'red' }}
                          onClick={() => handleToggleStatus(user.Status === 'actif' || user.ID_User, user.Status === 'Actif' || user.Status === true || user.Status === 1)}
                        >
                        <ListItemIcon sx={{ color: 'red' }}>
                            <PersonOffIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                          {user.Status === 'actif' || user.Status === 'Actif' || user.Status === true || user.Status === 1 ? 'Desactiver' : 'Activer'}
                        </ListItemText>
                        </MenuItem>
                        <MenuItem sx={{ color: 'red' }}>
                        <ListItemIcon sx={{ color: 'red' }}>
                            <History fontSize="small" />
                        </ListItemIcon>
                        <ListItemText onClick={() => handleOpenHis(user)}>Historique</ListItemText>
                        </MenuItem>
                        <MenuItem>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText onClick={() => handleDelete(user.ID_User)}>Supprimer</ListItemText>
                        </MenuItem>
                    </Menu>
                    </TableCell>
                </TableRow>
                );
              })
            )}
            
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography>{filteredUsers.length} entrées</Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': { borderColor: '#000' },
                '&.Mui-focused fieldset': { borderColor: 'red' },
              }
            }}>
              <Select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handleChangePage}
              variant="outlined"
              shape="rounded"
            />
          </Box>
        </Box>
      </Box>

      <Snackbar 
        open={snack.open} 
        autoHideDuration={3000} 
        onClose={handleCloseSnack} 
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert 
          onClose={handleCloseSnack} 
          severity={snack.type} 
          elevation={6} 
          variant="filled" 
          sx={{ width: '100%', bgcolor: snack.type === 'success' ? 'green' : 'red' }}
        >
          {snack.message}
        </MuiAlert>
      </Snackbar>

    <HistoriqueModal open={openhis} handleClose={handleCloseHis} user={selectedUser} />
    <AddModal open={openadd} handleClose={handleCloseAdd} />
    </Box>
  );
}